import { useEffect, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from '@/features/auth';
import { captureError, captureMessage } from '@/shared/utils';

const URL = import.meta.env.VITE_API_BASE_URL;

export const useSocketGlobalConnection = () => {
  const { accessToken } = useAuth();
  const [socket, setSocket] = useState<Socket>();
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>();

  useEffect(() => {
    if (accessToken) {
      const socketInstance = io(URL, {
        auth: {
          token: `Bearer ${accessToken}`,
        },
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 60000,
        autoConnect: true,
      });

      socketInstance.on('connect', () => {
        setIsConnected(true);
        setError(null);
        captureMessage('Socket connected');
      });

      socketInstance.on('disconnect', (reason) => {
        setIsConnected(false);
        captureMessage(`Socket disconnected: ${reason}`);
        if (reason === 'io server disconnect' || reason === 'transport close') {
          socketInstance.connect();
        }
      });

      socketInstance.on('connect_error', (error) => {
        setIsConnected(false);
        setError(error.message);
        captureError(error, {
          module: 'socket',
          action: 'connection_error',
        });
      });

      setSocket(socketInstance);
    }
    return () => {
      socket?.disconnect();
    };
  }, [accessToken]);

  return { socket, isConnected, error };
};
